import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SessionAction } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Button, Typography } from '@/ui';

import { useGetAppointmentsQuery } from '@/store/api/appointmentApi';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const SUPPORTED_ACTIONS: readonly string[] = [
  SessionAction.RESCHEDULE,
  SessionAction.CANCEL,
];

export const INVALID_SERIES_LINK_MESSAGE =
  'This link is not valid. Open the session from your upcoming sessions instead.';

export const EMPTY_SERIES_MESSAGE =
  'This series has no upcoming sessions left to change.';

interface SeriesOccurrence {
  id: string;
  startAt: string;
  timezone: string;
}

/**
 * Orders the occurrences a member may act on.
 *
 * MEN-4915: the calendar description for a recurring booking is written once on
 * the recurring master, so every occurrence shows the same body and its links
 * name the series rather than one session. That removed 13 to 25 sequential
 * Google writes per booking, which were failing 53.2% of the time. The cost is
 * that the link no longer knows which session the member clicked from, so this
 * page asks -- defaulting to the next one, which is what a reschedule or cancel
 * almost always means.
 *
 * MEN-4915 (review, d2b580b4): `lifecycleStatus` carries no time. A recurring
 * series keeps every past occurrence at `scheduled`, so filtering on lifecycle
 * alone offered sessions that had already happened and, because the rows sort
 * oldest-first, defaulted to one. Almost every real series has past
 * occurrences, so that fired on the first realistic link.
 *
 * The boundary is the appointment's own `currentSchedule.endAt`, NOT
 * `actionCapabilities`. The capability flags look authoritative and are not:
 * the browser projection computes them from booking and lifecycle status with
 * no clock at all (moments rpc_http_handler.go:2044-2047), so a past scheduled
 * occurrence still reports `reschedule.allowed: true`. Gating on them would
 * have reproduced this defect one layer up while reading as a fix.
 *
 * ENDED, not STARTED, is the cut: an occurrence that is under way stays
 * actionable, so a member whose session just began can still cancel it. That
 * matches the coach surfaces, which already treat a session as past only once
 * it has ended (features/coachSchedule/SessionInfoPopover.tsx and
 * views/ListView.tsx).
 */
export const upcomingSeriesOccurrences = (
  appointments: readonly {
    currentSchedule: { endAt: string; startAt: string; timezone: string };
    id: string;
    lifecycleStatus: string;
  }[],
  now: number,
): SeriesOccurrence[] =>
  appointments
    .filter((appointment) => appointment.lifecycleStatus === 'scheduled')
    .filter((appointment) => {
      const endsAt = Date.parse(appointment.currentSchedule.endAt);
      // An unparseable end is not evidence that the session is still to come.
      // Dropping it loses one row from a picker; keeping it can land a member
      // on a session that already happened.
      return !Number.isNaN(endsAt) && endsAt > now;
    })
    .map((appointment) => ({
      id: appointment.id,
      startAt: appointment.currentSchedule.startAt,
      timezone: appointment.currentSchedule.timezone,
    }))
    .sort((left, right) => left.startAt.localeCompare(right.startAt));

const isNotFound = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { status?: unknown }).status === 404;

const occurrenceLabel = (occurrence: SeriesOccurrence): string => {
  const parsed = new Date(occurrence.startAt);
  if (Number.isNaN(parsed.getTime())) return occurrence.startAt;
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'long',
    timeZone: occurrence.timezone,
    timeZoneName: 'short',
    weekday: 'long',
    year: 'numeric',
  }).format(parsed);
};

const ExternalSeriesSessionRedirect = () => {
  const { action, seriesId = '' } = useParams();
  const navigate = useNavigate();
  const { isUserLoading, user } = useCurrentUser();
  const [chosenId, setChosenId] = useState<string | null>(null);

  const linkIsValid =
    UUID_PATTERN.test(seriesId) &&
    typeof action === 'string' &&
    SUPPORTED_ACTIONS.includes(action);

  // Frozen at mount: `rangeStart` is part of the RTK Query cache key, so a
  // value recomputed each render would refetch the series on every render.
  // Read through `Date.now` rather than `new Date()` so this boundary and the
  // client-side one below share a single clock source and cannot disagree.
  const rangeStart = useMemo(() => new Date(Date.now()).toISOString(), []);

  // MEN-4915 (review, d2b580b4): this route is member-facing, so it must say so.
  // When no acting role is sent, the appointment list infers scope from the
  // signed-in user, and a dual-role user with a coach profile infers COACH
  // (backend appointments_controller.rb `viewer_scope`, the
  // `current_user.coach_profile.present?` branch). For a series where that same
  // person is the MEMBER, coach scope matches no rows, so the page reported the
  // series as having nothing left to change and silently blocked both actions.
  // Fixed rather than derived from the viewer: the route only ever serves a
  // member link, so inferring the role here would reintroduce the same guess.
  const query = useGetAppointmentsQuery(
    linkIsValid && !isUserLoading
      ? { actingRole: 'member', rangeStart, seriesId, userId: user.id }
      : skipToken,
  );

  // Two filters, both load-bearing. `rangeStart` makes the server exclude ended
  // occurrences against the authoritative stored schedule -- it compares
  // `current_end_at > rangeStart`, so it keeps an occurrence that is under way,
  // which is the policy this page wants. The client filter is not redundant
  // with it: `getAppointments` merges pages and deliberately retains previously
  // cached appointments while pagination is pending, so a row fetched before
  // this mount can reappear in `currentData` regardless of the range sent.
  //
  // Keyed on the data, not on a ticking clock, so the list does not re-filter
  // under a member mid-choice: an occurrence that ends while this page is open
  // stays listed until the query refetches. That is deliberate. The window is
  // one page visit, and the action page this hands off to validates the
  // appointment again, so the cost of a stale row is one extra hop rather than
  // a radio vanishing as someone reaches for it.
  const occurrences = useMemo(
    () =>
      upcomingSeriesOccurrences(
        query.currentData ? query.currentData.appointments : [],
        Date.now(),
      ),
    [query.currentData],
  );

  if (!linkIsValid) {
    return <Typography>{INVALID_SERIES_LINK_MESSAGE}</Typography>;
  }
  if (isUserLoading || query.isLoading || query.isFetching) {
    return <Typography>Loading your sessions...</Typography>;
  }
  // MEN-5570: Rails' MomentsSchedulingGate answers 404 when the signed-in
  // account has no Moments coach. In production that was a staff member whose
  // work account opened the link sent to their personal member account; the
  // generic retry copy hid that retrying could never work.
  if (query.isError && isNotFound(query.error)) {
    return (
      <Typography>
        This link isn&apos;t for the account you&apos;re signed in as
        {user.email ? ` (${user.email})` : ''}. Sign out, then sign in with the
        account that received the calendar invite.
      </Typography>
    );
  }
  if (query.isError) {
    return (
      <Typography>Unable to load this series. Please try again.</Typography>
    );
  }
  if (occurrences.length === 0) {
    return <Typography>{EMPTY_SERIES_MESSAGE}</Typography>;
  }

  // The next occurrence is the default because that is what a reschedule or a
  // cancel from a calendar invite almost always means.
  const firstOccurrence = occurrences[0];
  if (!firstOccurrence) {
    return <Typography>{EMPTY_SERIES_MESSAGE}</Typography>;
  }
  const selectedId = chosenId ?? firstOccurrence.id;
  const heading =
    action === SessionAction.CANCEL
      ? 'Which session do you want to cancel?'
      : 'Which session do you want to reschedule?';

  return (
    <div>
      <Typography>{heading}</Typography>
      <fieldset>
        <legend>{heading}</legend>
        {occurrences.map((occurrence) => (
          <div key={occurrence.id}>
            <input
              checked={occurrence.id === selectedId}
              id={`series-occurrence-${occurrence.id}`}
              name="series-occurrence"
              type="radio"
              value={occurrence.id}
              onChange={() => setChosenId(occurrence.id)}
            />
            <label htmlFor={`series-occurrence-${occurrence.id}`}>
              {occurrenceLabel(occurrence)}
            </label>
          </div>
        ))}
      </fieldset>
      <Button
        variant="contained"
        onClick={() =>
          navigate(`/appointments/external/${selectedId}/${action}`, {
            replace: true,
          })
        }
      >
        Continue
      </Button>
    </div>
  );
};

export default ExternalSeriesSessionRedirect;
